/* eslint-disable no-constant-condition */
import { push } from 'react-router-redux'
import { SubmissionError } from 'redux-form/immutable'
import { call, fork, put, race, select, take } from 'redux-saga/effects'
import { takeEvery } from 'redux-saga'
import * as addressbooks from '../addressbook/addressbooks'
import * as forms from './forms'
import * as locations from '../addressbook/locations'
import * as additions from './additions'
import * as order from '../order/order'
import * as orders from '../orders/orders'
import * as tracking from '../orders/tracking'
import { getTrackingFailure, getTrackingSuccess } from '../orders/tracking'
import * as products from '../order/products'
import * as regions from './regions'
import * as services from './services'
import * as session from './session'
import { SESSION_INVALIDATE } from './session'
import * as tenants from './tenants'
import * as settings from '../settings/settings'
import {
    getSoftDeleteFailure,
    getSoftDeleteSuccess,
    updateEmailFailure,
    updateEmailSuccess,
    updatePasswordFailure,
    updatePasswordSuccess
} from '../settings/settings'
import * as certificate from '../certificate/certificate'
import * as spinner from './spinner'
import * as steps from '../order/steps'
import * as errors from './errors'
import api from './api'
import formatErrors from '../../lib/formatErrors'
import validators from '../order/validators'

// TODO: This needs to be split into features.

// Helpers
const wait = ms => (
    new Promise(resolve => {
        setTimeout(() => resolve(true), ms);
    })
);

// Steps
function* gotoStep(action) {
    const {from, to, validate} = action;
    const list = yield select(steps.getEnabledSteps);

    try {
        if (validate && validators[from]) {
            yield put(steps.validateStep(from));

            const {result, errormessage, error, fields} = yield call(validators[from], yield select(order.getOrder));

            if (!result) {
                yield put(steps.failStep(from, {error, fields}, errormessage));
                return;
            }
        }

        yield put(steps.completeStep(from));

        const nextStep = list.get(to);
        yield put(push(nextStep.get('route')));
    } catch (error) {
        yield put(steps.failStep(from, {error}));
        throw error;
    }
}

/**
 * @param error
 * @return boolean Whether the error has been handled. If so, the caller function should not handle the error.
 */
function* tryHandleError(error) {
    if (error.status === 401) {
        // User is not authenticated but should be. Invalidate the session.
        yield put({type: SESSION_INVALIDATE, payload: {}});

        return false;
    }

    if (error.status === 500 || error.code === 'ECONNABORTED') {
        // If we receive an internal server error, something went horribly wrong.
        // Or an api request has timed out (cancelled by axios after timeout has been exceeded).
        yield put(errors.setGlobalError(error));

        // Stop the code execution as this error is potentially unrecoverable.
        return true;
    }

    if (error.status === 503) {
        // If we receive an 503, takeback is in maintenance mode or otherwise unavailable.
        // Set maintenance flag in session.
        yield put(session.setMaintenance(true));
        return true;
    }

    // If the error has not been handled at this point, we expect this to be an expected error and the action failure
    // reducer is supposed to handle the error correctly.
    return false;
}

export function* watchStepNavigation() {
    while (true) {
        // Step navigation is blocking by default
        const action = yield take(steps.ACTION_GOTO_STEP);
        yield call(gotoStep, action);
    }
}

function* fetchOrderLabel(orderId) {
    let interval = 250;
    while (true) {
        const winner = yield race({
            stopped: take(orders.FETCH_ORDER_LABEL_STOP),
            tick: call(wait, interval += 250),
        });

        if (winner.stopped) {
            break;
        }

        const {response, error} = yield api.order.getLabel({order: orderId});
        if (response) {
            if (response.data.get('path')) {
                yield put(orders.fetchOrderLabelSuccess(orderId, response.data));
                break;
            } else if (response.data.get('error')) {
                yield put(orders.fetchOrderLabelFailure(orderId, response.data));
            }
        } else if (!(yield call(tryHandleError, error))) {
            yield put(orders.fetchOrderLabelFailure(orderId));
        }
    }
}

function* watchFetchOrderLabel() {
    while (true) {
        const {orderId} = yield take(orders.FETCH_ORDER_LABEL);
        const winner = yield race({
            failed: take(orders.FETCH_ORDER_LABEL_FAILURE),
            fetch: call(fetchOrderLabel, orderId),
            timeout: call(wait, 35 * 1000),
        });

        if (winner.timeout) {
            yield put(orders.fetchOrderLabelFailure(orderId));
        }
    }
}

// Products
export function* getProducts(action) {
    yield put(spinner.startSpinner(action.spinner || 'order'));

    const {response, error} = yield call(api.products.get, action.params);

    if (response) {
        yield put(products.getProductsSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(products.getProductsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order'));
}

export function* watchGetProducts() {
    yield* takeEvery(products.GET_PRODUCTS, getProducts);
}

// Regions
export function* getRegions(action) {
    yield put(spinner.startSpinner(action.spinner || 'order'));
    const {response, error} = yield call(api.regions.get);

    if (response) {
        yield put(regions.getRegionsSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(regions.getRegionsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order'));
}

export function* watchGetRegions() {
    yield* takeEvery(regions.GET_REGIONS, getRegions);
}

// Services
export function* getServices(action) {
    yield put(spinner.startSpinner(action.spinner || 'order'));
    const {response, error} = yield call(api.services.get, action.params);

    if (response) {
        yield put(services.getServicesSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(services.getServicesFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order'));
}

export function* watchGetServices() {
    yield* takeEvery(services.GET_SERVICES, getServices);
}

// Locations & Addresses
export function* getLocations(action) {
    yield put(spinner.startSpinner(action.spinner || 'locations'));

    const {response, error} = yield call(api.locations.get);

    if (response) {
        yield put(locations.getLocationsSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(locations.getLocationsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'locations'));
}

export function* watchGetLocations() {
    yield* takeEvery(locations.GET_LOCATIONS, getLocations);
}

// Locations Count
export function* getLocationsCount(action) {
    yield put(spinner.startSpinner(action.spinner || 'locations'));

    const {response, error} = yield call(api.locations.count);

    if (response) {
        yield put(locations.getLocationsCountSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(locations.getLocationsCountFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'locations'));
}

export function* watchGetLocationsCount() {
    yield* takeEvery(locations.GET_LOCATIONS_COUNT, getLocationsCount);
}

// Recent Locations
export function* getRecentLocations(action) {
    yield put(spinner.startSpinner(action.spinner || 'recent-locations'));

    const {response, error} = yield call(api.locations.getRecent, action.params.page);

    if (response) {
        yield put(locations.getRecentLocationsSuccess(response.data, action.append));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(locations.getRecentLocationsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'recent-locations'));
}

export function* watchGetRecentLocations() {
    yield* takeEvery(locations.GET_RECENT_LOCATIONS, getRecentLocations);
}

// Location Search
export function* searchLocationsByNumber(action) {
    yield put(spinner.startSpinner(action.spinner || 'location-search'));

    const {response, error} = yield call(api.locations.findByNumber, action.searchTerm);

    if (response) {
        let searchResults = [];

        // Handle multiple results response structure
        if (response.data.locations) {
            searchResults = response.data.locations;
        } else {
            // Handle legacy single result (convert to array)
            searchResults = [response.data];
        }

        yield put(locations.searchLocationsByNumberSuccess(searchResults, action.searchTerm));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(locations.searchLocationsByNumberFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'location-search'));
}

export function* watchSearchLocationsByNumber() {
    yield* takeEvery(locations.SEARCH_LOCATIONS_BY_NUMBER, searchLocationsByNumber);
}

// Additions
export function* getAdditions(action) {
    yield put(spinner.startSpinner(action.spinner || 'additions'));
    const {response, error} = yield call(api.additions.get, action.data);

    if (response) {
        yield put(additions.getAdditionsSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(additions.getAdditionsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'additions'));
}

export function* watchGetAdditions() {
    yield* takeEvery(additions.GET_ADDITIONS, getAdditions);
}

// Orders
export function* getOrders() {
    // TODO: Right now this is hard-coded
    yield put(spinner.startSpinner('orders'));

    const [{response: orderResponse, error: orderError}, {response: serviceResponse, error: serviceError}] = yield [
        call(api.orders.get),
        call(api.services.get),
    ];

    if (orderResponse) {
        yield put(orders.getOrdersSuccess(orderResponse.data));
    } else if (!(yield call(tryHandleError, orderError))) {
        yield put(orders.getOrderFailure(orderError));
    }

    if (serviceResponse) {
        yield put(services.getServicesSuccess(serviceResponse.data));
    } else if (!(yield call(tryHandleError, serviceError))) {
        yield put(services.getServicesFailure(serviceError));
    }

    yield put(spinner.stopSpinner('orders'));
}

export function* watchGetOrders() {
    yield* takeEvery(orders.GET_ORDERS, getOrders);
}


// Order /orders/:code
export function* getOrder(action) {
    // TODO: Right now this is hard-coded
    yield put(spinner.startSpinner(action.spinner || 'order.details'));
    const {response: orderResponse, error: orderError} = yield call(api.order.get, action.params);

    if (orderResponse) {
        yield put(orders.getOrderSuccess(orderResponse.data));
        yield put(orders.fetchOrderLabel(orderResponse.data.get('id')));
    } else if (!(yield call(tryHandleError, orderError))) {
        yield put(orders.getOrderFailure(orderError));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order.details'));
}

export function* watchGetOrder() {
    yield* takeEvery(orders.GET_ORDER, getOrder);
}

// Order
function* dispatchOrder(action) {
    const newOrder = action.order;
    const stepList = yield select(steps.getEnabledSteps);

    yield put(spinner.startSpinner('order'));
    yield put(steps.completeStep('summary'));

    const confirmationStep = stepList.get('confirmation');
    yield put(push(confirmationStep.get('route')));

    const data = order.transformForDispatch(newOrder);
    const {response, error} = yield api.order.dispatch(data);

    if (response) {
        const {response: orderResponse, error: orderError} =
            yield call(api.order.get, {code: response.data.get('code')});
        if (orderResponse) {
            yield put(orders.getOrderSuccess(orderResponse.data));
            yield put(order.dispatchSuccess(response.data));
            if (orderResponse.data.getIn(['labelStatus', 'required'], false)) {
                yield put(orders.fetchOrderLabel(response.data.get('id')));
            }
        } else if (!(yield call(tryHandleError, orderError))) {
            yield put(orders.getOrderFailure(orderError));
        }
    } else if (!(yield call(tryHandleError, error))) {
        yield put(order.dispatchFailure(newOrder, error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order'));
}


export function* existsOrderByCode(action) {
    yield put(spinner.startSpinner(action.spinner || 'orders.tracking'));

    const [
        {response: existsResponse, error: existsError},
    ] = yield [
        call(api.order.tracking, action.payload.get('code')),
    ];

    if (existsResponse) {
        yield put(getTrackingSuccess(existsResponse.data));
    } else if (!(yield call(tryHandleError, existsError))) {
        yield put(getTrackingFailure(existsError));
    }

    yield put(spinner.stopSpinner(action.spinner || 'orders.tracking'));
}

export function* watchDispatchOrder() {
    while (true) {
        const action = yield take(order.DISPATCH_ORDER);
        yield call(dispatchOrder, action);
    }
}

export function* watchGetOrderTracking() {
    yield* takeEvery(tracking.GET_TRACKING, existsOrderByCode);
}

// Locations & Addresses
export function* getAddressbooks(action) {
    yield put(spinner.startSpinner(action.spinner || 'order'));

    const {response, error} = yield call(api.addressbooks.get);

    if (response) {
        yield put(addressbooks.getAddressbooksSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.getAddressbooksFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'order'));
}

export function* watchGetAddressbooks() {
    yield* takeEvery(addressbooks.GET_ADDRESSBOOKS, getAddressbooks);
}

export function* createAddressbook(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook'));

    const {response, error} = yield call(api.addressbooks.create, action.payload.get('name'));

    if (response) {
        yield put(addressbooks.createAddressbookSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.createAddressbookFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook'));
}

export function* watchCreateAddressbook() {
    yield* takeEvery(addressbooks.CREATE_ADDRESSBOOK, createAddressbook);
}

export function* shareAddressbook(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook_share'));

    const {response, error} = yield call(api.addressbooks.share, action.payload.get('addressbook'), action.payload.get('email'));

    if (response) {
        yield put(addressbooks.inviteAddressbookSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.inviteAddressbookFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook_share'));
}

export function* watchShareAddressbook() {
    yield* takeEvery(addressbooks.INVITE_ADDRESSBOOK, shareAddressbook);
}

export function* promoteAddressbookUser(action) {
    yield put(spinner.startSpinner(action.spinner || ('addressbook_promote_' + action.payload.get('user'))));

    const {response, error} = yield call(api.addressbooks.promote, action.payload.get('addressbook'), action.payload.get('user'));

    if (response) {
        yield put(addressbooks.promoteAddressbookUserSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.promoteAddressbookUserFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || ('addressbook_promote_' + action.payload.get('user'))));
}

export function* watchPromoteAddressbook() {
    yield* takeEvery(addressbooks.PROMOTE_ADDRESSBOOK_USER, promoteAddressbookUser);
}

export function* demoteAddressbookUser(action) {
    yield put(spinner.startSpinner(action.spinner || ('addressbook_demote_' + action.payload.get('user'))));

    const {response, error} = yield call(api.addressbooks.demote, action.payload.get('addressbook'), action.payload.get('user'));

    if (response) {
        yield put(addressbooks.demoteAddressbookUserSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.demoteAddressbookUserFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || ('addressbook_demote_' + action.payload.get('user'))));
}

export function* watchDemoteAddressbook() {
    yield* takeEvery(addressbooks.DEMOTE_ADDRESSBOOK_USER, demoteAddressbookUser);
}

export function* removeAddressbookUser(action) {
    yield put(spinner.startSpinner(action.spinner || ('addressbook_remove_' + action.payload.get('user'))));

    const {response, error} = yield call(api.addressbooks.remove, action.payload.get('addressbook'), action.payload.get('user'));

    if (response) {
        yield put(addressbooks.removeAddressbookUserSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.removeAddressbookUserFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || ('addressbook_remove_' + action.payload.get('user'))));
}

export function* watchRemoveAddressbookUser() {
    yield* takeEvery(addressbooks.REMOVE_ADDRESSBOOK_USER, removeAddressbookUser);
}

export function* leaveAddressbook(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook_leave'));

    const {response, error} = yield call(api.addressbooks.leave, action.payload.get('addressbook'));

    if (response) {
        yield put(addressbooks.leaveAddressbookSuccess(response.data));
        yield put(locations.getLocations());
    } else if (!(yield call(tryHandleError, error))) {
        yield put(addressbooks.leaveAddressbookFailure(error.data.fields));
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook_leave'));
}

export function* watchLeaveAddressbook() {
    yield* takeEvery(addressbooks.LEAVE_ADDRESSBOOK, leaveAddressbook);
}

function* createLocationForm(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook'));
    const {response, error} = yield call(api.locations.create, action.payload);

    if (response) {
        yield put({type: locations.CREATE_LOCATION_SUCCESS, payload: response.data});
        yield put({type: locations.GET_LOCATIONS}); // Update locations for address book.
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: locations.CREATE_LOCATION_FAILURE,
            error_type: error.data ? error.data.get('error') : null,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook'));
}

export function* watchCreateLocationFormSaga() {
    yield* takeEvery(locations.CREATE_LOCATION_SUBMIT, createLocationForm);
}

function* updateLocationForm(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook'));
    const {response, error} = yield call(api.locations.update, action.payload);

    if (response) {
        yield put({type: locations.UPDATE_LOCATION_SUCCESS, payload: response.data});
        yield put({type: locations.GET_LOCATIONS}); // Update locations for address book.
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: locations.UPDATE_LOCATION_FAILURE,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook'));
}

export function* watchUpdateLocationFormSaga() {
    yield* takeEvery(locations.UPDATE_LOCATION_SUBMIT, updateLocationForm);
}

export function* hideLocation(action) {
    yield put(spinner.startSpinner(action.spinner || 'addressbook'));
    console.log(action, api.locations.hide);
    const {response, error} = yield call(api.locations.hide, action.id);

    if (response) {
        yield put({type: locations.HIDE_LOCATION_SUCCESS, payload: response.data});
        window.location.reload(); // Currently I can't seem to find another way to reliably update the address list.
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: locations.HIDE_LOCATION_FAILURE,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'addressbook'));
}

export function* watchHideLocationSaga() {
    yield* takeEvery(locations.HIDE_LOCATION_SUBMIT, hideLocation);
}

// User
export function* softDelete(action) {
    yield put(spinner.startSpinner(action.spinner || 'user.softDelete'));
    const [
        {response: existsResponse, error: existsError},
    ] = yield [
        call(api.user.softDelete, action.payload.get('code'), action.payload.get('email')),
    ];

    if (existsResponse) {
        yield put(getSoftDeleteSuccess(existsResponse.data));
    } else if (!(yield call(tryHandleError, existsError))) {
        const errorMessage = existsError.data ? existsError.data.toJSON() : {};
        yield put(getSoftDeleteFailure(errorMessage));
    }

    yield put(spinner.stopSpinner(action.spinner || 'user.softDelete'));
}

export function* watchUserSoftDelete() {
    yield* takeEvery(settings.GET_SOFT_DELETE, softDelete);
}

// Tenant
export function* getTenants(action) {
    yield put(spinner.startSpinner(action.spinner || 'tenants'));
    const {response, error} = yield call(api.tenants.get);

    if (response) {
        yield put(tenants.getTenantsSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(tenants.getTenantsFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'tenants'));
}

export function* watchGetTenants() {
    yield* takeEvery(tenants.GET_TENANTS, getTenants);
}


// Account
function* loginForm(action) {
    yield put(spinner.startSpinner(action.spinner || 'login'));
    const {response, error} = yield call(api.auth.login, action.payload);

    if (response) {
        yield put({type: session.LOGIN_SUCCESS, payload: response.data});
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: session.LOGIN_FAILURE,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'login'));
}

export function* watchLoginFormSaga() {
    yield* takeEvery(session.LOGIN_SUBMIT, loginForm);
}

function* logout(action) {
    yield put(spinner.startSpinner(action.spinner || 'logout'));
    const {response, error} = yield call(api.auth.logout, action.payload);

    if (response) {
        yield put({type: session.LOGOUT_SUCCESS, payload: {
            authenticated: false,
            admin: false,
            loggedOut: true,
            user: null,
            csrf: null,
        }});
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: session.LOGOUT_FAILURE,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'logout'));
}

export function* watchLogoutSaga() {
    yield* takeEvery(session.LOGOUT_SUBMIT, logout);
}

function* registerForm(action) {
    yield put(spinner.startSpinner(action.spinner || 'register'));
    const {response, error} = yield call(api.auth.register, action.payload);

    if (response) {
        yield put({type: session.REGISTER_SUCCESS, payload: response.data});
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: session.REGISTER_FAILURE,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'register'));
}

export function* watchRegisterFormSaga() {
    yield* takeEvery(session.REGISTER_SUBMIT, registerForm);
}

function* activateTenant(action) {
    yield put(spinner.startSpinner(action.spinner || 'activate_tenant'));
    const {response, error} = yield call(api.auth.activateTenant, action.payload);

    if (response) {
        yield put({type: session.TENANT_ACTIVATE_SUCCESS, payload: response.data.payload});
    } else if (!(yield call(tryHandleError, error))) {
        yield put({
            type: session.TENANT_ACTIVATE_ERROR,
            errors: error.data ? error.data.get('fields').toJSON() : {},
        });
    }

    yield put(spinner.stopSpinner(action.spinner || 'activate_tenant'));
}

export function* watchActivateTenantSaga() {
    yield* takeEvery(session.TENANT_ACTIVATE, activateTenant);
}

// Certificate
export function* getCertificate(action) {
    yield put(spinner.startSpinner(action.spinner || 'certificate'));
    const {response, error} = yield call(api.certificate.get);

    if (response) {
        yield put(certificate.getCertificateSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(certificate.getCertificateFailure(error));
    }

    yield put(spinner.stopSpinner(action.spinner || 'certificate'));
}

export function* watchGetCertificate() {
    yield* takeEvery(certificate.GET_CERTIFICATE, getCertificate);
}

// Update Password
export function* updatePassword(action) {
    yield put(spinner.startSpinner(action.spinner || 'user.updatePassword'));

    const {response: response, error: error} =
        yield call(api.user.updatePassword, action.payload.get('current_password'), action.payload.get('password'), action.payload.get('password_confirmation'));

    if (response) {
        yield put(updatePasswordSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(updatePasswordFailure(error.data ? error.data.get('fields').toJSON() : {}));
    }

    yield put(spinner.stopSpinner(action.spinner || 'user.updatePassword'));
}

export function* watchUpdatePassword() {
    yield* takeEvery(settings.UPDATE_PASSWORD, updatePassword);
}

// Update Email
export function* updateEmail(action) {
    yield put(spinner.startSpinner(action.spinner || 'user.updateEmail'));

    const {response: response, error: error} =
        yield call(api.user.updateEmail, action.payload.get('email'));

    if (response) {
        yield put(updateEmailSuccess(response.data));
    } else if (!(yield call(tryHandleError, error))) {
        yield put(updateEmailFailure(error.data ? error.data.get('fields').toJSON() : {}));
    }

    yield put(spinner.stopSpinner(action.spinner || 'user.updateEmail'));
}

export function* watchUpdateEmail() {
    yield* takeEvery(settings.UPDATE_EMAIL, updateEmail);
}


// Forms
function* formSubmitSaga({payload: {submitAction, successAction, failureAction, values, resolve, reject}}) {
    yield put({type: submitAction, payload: values});

    const {success, failure} = yield race({
        success: take(successAction),
        failure: take(failureAction),
    });

    if (success) {
        resolve(success.payload);
    } else {
        if (failure.errors) {
            reject(new SubmissionError(Object.assign({}, formatErrors(failure.errors), {_error: failure.error_type})));
        } else {
            reject(failure.payload);
        }
    }
}

export function* watchFormSubmitSaga() {
    yield* takeEvery(forms.FORM_SUBMIT, formSubmitSaga);
}

export default function* root() {
    yield [
        fork(watchCreateLocationFormSaga),
        fork(watchUpdateLocationFormSaga),
        fork(watchDispatchOrder),
        fork(watchFetchOrderLabel),
        fork(watchFormSubmitSaga),
        fork(watchGetAddressbooks),
        fork(watchCreateAddressbook),
        fork(watchShareAddressbook),
        fork(watchPromoteAddressbook),
        fork(watchDemoteAddressbook),
        fork(watchRemoveAddressbookUser),
        fork(watchLeaveAddressbook),
        fork(watchUserSoftDelete),
        fork(watchGetAdditions),
        fork(watchGetLocations),
        fork(watchGetLocationsCount),
        fork(watchGetRecentLocations),
        fork(watchSearchLocationsByNumber),
        fork(watchGetOrderTracking),
        fork(watchGetOrder),
        fork(watchGetOrders),
        fork(watchGetProducts),
        fork(watchGetCertificate),
        fork(watchGetRegions),
        fork(watchGetServices),
        fork(watchLoginFormSaga),
        fork(watchLogoutSaga),
        fork(watchGetTenants),
        fork(watchRegisterFormSaga),
        fork(watchStepNavigation),
        fork(watchUpdatePassword),
        fork(watchUpdateEmail),
        fork(watchActivateTenantSaga),
        fork(watchHideLocationSaga),
    ];
}
